/**
 * Dashboard Theme System
 *
 * Centralized color and styling system for the WMS dashboard.
 * Provides consistent theming for widgets, charts, status indicators,
 * and threshold-based color coding.
 *
 * All colors are Tailwind CSS classes for dark mode optimization.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

// ========================================================
// Status Colors
// ========================================================

export type StatusLevel = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export const STATUS_COLORS: Record<StatusLevel, {
  bg: string;
  text: string;
  border: string;
  bgLight: string;
  icon: string;
}> = {
  success: {
    bg: 'bg-emerald-500',
    text: 'text-emerald-400',
    border: 'border-emerald-500',
    bgLight: 'bg-emerald-500/20',
    icon: 'text-emerald-400',
  },
  warning: {
    bg: 'bg-amber-500',
    text: 'text-amber-400',
    border: 'border-amber-500',
    bgLight: 'bg-amber-500/20',
    icon: 'text-amber-400',
  },
  danger: {
    bg: 'bg-red-500',
    text: 'text-red-400',
    border: 'border-red-500',
    bgLight: 'bg-red-500/20',
    icon: 'text-red-400',
  },
  info: {
    bg: 'bg-blue-500',
    text: 'text-blue-400',
    border: 'border-blue-500',
    bgLight: 'bg-blue-500/20',
    icon: 'text-blue-400',
  },
  neutral: {
    bg: 'bg-gray-500',
    text: 'text-gray-400',
    border: 'border-gray-500',
    bgLight: 'bg-gray-500/20',
    icon: 'text-gray-400',
  },
};

// ========================================================
// Threshold-based Color Functions
// ========================================================

/**
 * Get status level based on value vs target.
 * For "higher is better" metrics (e.g., pick rate, accuracy).
 */
export function getThresholdStatus(
  value: number,
  target: number,
  options?: {
    warningThreshold?: number; // Percent of target (default 0.85)
    dangerThreshold?: number;  // Percent of target (default 0.70)
  }
): StatusLevel {
  const warningAt = target * (options?.warningThreshold ?? 0.85);
  const dangerAt = target * (options?.dangerThreshold ?? 0.70);

  if (value >= target) return 'success';
  if (value >= warningAt) return 'warning';
  if (value >= dangerAt) return 'danger';
  return 'danger';
}

/**
 * Get status level for "lower is better" metrics (e.g., error rate, time).
 */
export function getThresholdStatusInverse(
  value: number,
  target: number,
  options?: {
    warningThreshold?: number; // Multiplier of target (default 1.15)
    dangerThreshold?: number;  // Multiplier of target (default 1.30)
  }
): StatusLevel {
  const warningAt = target * (options?.warningThreshold ?? 1.15);
  const dangerAt = target * (options?.dangerThreshold ?? 1.30);

  if (value <= target) return 'success';
  if (value <= warningAt) return 'warning';
  return 'danger';
}

/**
 * Get Tailwind color class for a threshold value
 */
export function getThresholdColor(value: number, target: number, inverse = false): string {
  const status = inverse
    ? getThresholdStatusInverse(value, target)
    : getThresholdStatus(value, target);
  return STATUS_COLORS[status].text;
}

/**
 * Get Tailwind bg color class for a threshold value
 */
export function getThresholdBgColor(value: number, target: number, inverse = false): string {
  const status = inverse
    ? getThresholdStatusInverse(value, target)
    : getThresholdStatus(value, target);
  return STATUS_COLORS[status].bgLight;
}

// ========================================================
// Utilization / Capacity Colors
// ========================================================

export type UtilizationLevel = 'low' | 'optimal' | 'high' | 'critical';

/**
 * Get utilization level from percent (0-100)
 */
export function getUtilizationLevel(percent: number): UtilizationLevel {
  if (percent < 30) return 'low';
  if (percent < 70) return 'optimal';
  if (percent < 90) return 'high';
  return 'critical';
}

export const UTILIZATION_COLORS: Record<UtilizationLevel, {
  bg: string;
  text: string;
  fill: string;
  label: string;
}> = {
  low: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    fill: '#3b82f6',
    label: 'Lav',
  },
  optimal: {
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    fill: '#10b981',
    label: 'Optimal',
  },
  high: {
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    fill: '#f59e0b',
    label: 'H\u00f8y',
  },
  critical: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    fill: '#ef4444',
    label: 'Kritisk',
  },
};

// ========================================================
// Chart Colors - 10 distinct colors for dark mode
// ========================================================

export const CHART_COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#f97316', // orange-500
  '#ec4899', // pink-500
  '#84cc16', // lime-500
  '#6366f1', // indigo-500
] as const;

/**
 * Get chart color by index (wraps around if > 10)
 */
export function getChartColor(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length];
}

// ========================================================
// Chart Gradient Definitions (for Recharts area charts)
// ========================================================

export const CHART_GRADIENTS = {
  blue: { start: '#3b82f6', end: 'rgba(59, 130, 246, 0.05)' },
  emerald: { start: '#10b981', end: 'rgba(16, 185, 129, 0.05)' },
  amber: { start: '#f59e0b', end: 'rgba(245, 158, 11, 0.05)' },
  red: { start: '#ef4444', end: 'rgba(239, 68, 68, 0.05)' },
  violet: { start: '#8b5cf6', end: 'rgba(139, 92, 246, 0.05)' },
  cyan: { start: '#06b6d4', end: 'rgba(6, 182, 212, 0.05)' },
} as const;

// ========================================================
// Severity / Priority Colors
// ========================================================

export type SeverityLevel = 'low' | 'medium' | 'high' | 'critical' | 'urgent';

export const SEVERITY_COLORS: Record<SeverityLevel, {
  bg: string;
  text: string;
  border: string;
  dot: string;
  label: string;
}> = {
  low: {
    bg: 'bg-gray-500/20',
    text: 'text-gray-400',
    border: 'border-gray-500',
    dot: 'bg-gray-400',
    label: 'Lav',
  },
  medium: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500',
    dot: 'bg-blue-400',
    label: 'Middels',
  },
  high: {
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    border: 'border-amber-500',
    dot: 'bg-amber-400',
    label: 'H\u00f8y',
  },
  critical: {
    bg: 'bg-red-500/20',
    text: 'text-red-400',
    border: 'border-red-500',
    dot: 'bg-red-400',
    label: 'Kritisk',
  },
  urgent: {
    bg: 'bg-red-600/30',
    text: 'text-red-300',
    border: 'border-red-400',
    dot: 'bg-red-300 animate-pulse',
    label: 'Haster',
  },
};

// ========================================================
// Trend Colors
// ========================================================

export type TrendDirection = 'up' | 'down' | 'stable';

/**
 * Get trend color - for "up is good" metrics
 */
export function getTrendColor(trend: TrendDirection, upIsGood = true): string {
  if (trend === 'stable') return 'text-gray-400';
  if (trend === 'up') return upIsGood ? 'text-emerald-400' : 'text-red-400';
  return upIsGood ? 'text-red-400' : 'text-emerald-400';
}

// ========================================================
// Leaderboard / Gamification Colors
// ========================================================

export const PODIUM_COLORS = {
  gold: {
    bg: 'bg-yellow-500/20',
    text: 'text-yellow-400',
    border: 'border-yellow-500',
    icon: 'text-yellow-400',
    gradient: 'from-yellow-500/30 to-yellow-600/10',
  },
  silver: {
    bg: 'bg-gray-300/20',
    text: 'text-gray-300',
    border: 'border-gray-400',
    icon: 'text-gray-300',
    gradient: 'from-gray-300/30 to-gray-400/10',
  },
  bronze: {
    bg: 'bg-orange-600/20',
    text: 'text-orange-400',
    border: 'border-orange-500',
    icon: 'text-orange-400',
    gradient: 'from-orange-500/30 to-orange-600/10',
  },
} as const;

/**
 * Get podium color by rank (1, 2, 3)
 */
export function getPodiumColor(rank: number) {
  if (rank === 1) return PODIUM_COLORS.gold;
  if (rank === 2) return PODIUM_COLORS.silver;
  if (rank === 3) return PODIUM_COLORS.bronze;
  return null;
}

// ========================================================
// Activity Type Colors
// ========================================================

export const ACTIVITY_TYPE_COLORS: Record<string, string> = {
  picking: '#3b82f6',   // blue
  receiving: '#8b5cf6', // violet
  putaway: '#10b981',   // emerald
  transfer: '#f59e0b',  // amber
  adjustment: '#ef4444', // red
  idle: '#6b7280',      // gray
};

// ========================================================
// Widget Card Styling
// ========================================================

export const WIDGET_CARD = {
  base: 'bg-gray-800 rounded-lg border-2 border-gray-700',
  hover: 'hover:border-gray-600',
  selected: 'border-blue-500 ring-2 ring-blue-500/30',
  inner: 'bg-gray-700/30 rounded-lg',
  innerHover: 'hover:bg-gray-700/50',
} as const;

// ========================================================
// TV Mode Scaling
// ========================================================

export const TV_SCALE = {
  text: {
    sm: 'text-sm md:text-base lg:text-lg',
    base: 'text-base md:text-lg lg:text-xl',
    lg: 'text-lg md:text-xl lg:text-2xl',
    xl: 'text-xl md:text-2xl lg:text-3xl',
    '2xl': 'text-2xl md:text-3xl lg:text-4xl',
    '4xl': 'text-4xl md:text-5xl lg:text-6xl',
    '6xl': 'text-6xl md:text-7xl lg:text-8xl',
  },
  spacing: {
    sm: 'p-2 md:p-3 lg:p-4',
    base: 'p-3 md:p-4 lg:p-6',
    lg: 'p-4 md:p-6 lg:p-8',
  },
  gap: {
    sm: 'gap-2 md:gap-3 lg:gap-4',
    base: 'gap-3 md:gap-4 lg:gap-6',
    lg: 'gap-4 md:gap-6 lg:gap-8',
  },
} as const;
